import type { ApiClient } from '@carbonwebmail/api-client'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'
import { Alert } from '../../ui/Alert.js'
import { Field } from '../../ui/Field.js'
import { SplitPane } from '../../ui/SplitPane.js'
import { Attachments } from './Attachments.js'
import { Conversations } from './Conversations.js'
import { LearnedBrand } from './LearnedBrand.js'
import type { DesignerState, Turn } from './useDesigner.js'
import { useDesigner } from './useDesigner.js'

/**
 * Describing an email and watching one get designed.
 *
 * The conversation on the left, the design on the right, and the two things
 * worth doing with it underneath: keep it as a template, or take it into a
 * campaign. It is a designer rather than a chat — the point is the thing on the
 * right, and the messages are how it got there.
 *
 * **Nothing here is saved until somebody saves it.** A reload loses the thread,
 * which is the honest cost of a feature with no half-finished state to expire
 * or resume. What matters is one press from being a template.
 *
 * **The preview is sandboxed and shows the markup that was actually returned**,
 * already put through the allowlist on the server. What somebody approves here
 * is what gets sent — a preview showing something that renders away later is a
 * preview that lied.
 *
 * **It fills the window, and the divider between the two moves.** This is a
 * workspace rather than a page of rows: the thing being judged is an email, and
 * a fixed 560px box on a tall screen showed a third of one under a screenful of
 * nothing. How much room the conversation deserves against the design changes
 * with what you are doing — writing the brief, or looking at what came back —
 * so it is dragged rather than decided here.
 */
export function DesignerPage({ client }: { readonly client: ApiClient }) {
  const designer = useDesigner({ client })
  /** Whether the saved list is open. This screen only — nothing to remember. */
  const [showing, setShowing] = useState(true)
  const [instruction, setInstruction] = useState('')

  const ask = useCallback(() => {
    const said = instruction.trim()
    if (said === '' || designer.state.status === 'thinking') return
    setInstruction('')
    void designer.send(said)
  }, [designer, instruction])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="mb-4 flex-none">
        <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">Design with AI</h1>
        <p className="mt-1 text-[13px] text-t2">
          Describe the email you want. What comes back is ordinary email markup — keep it as a
          template, or take it straight into a campaign and edit it with blocks.
        </p>
      </header>

      <div className="flex-none">
        <Problem state={designer.state} />
      </div>

      {/*
        The list sits outside the split, so dragging the divider between the
        conversation and the preview does not resize it — it is navigation, not
        one of the two things being compared.
      */}
      <div className="flex min-h-0 flex-1 gap-3">
        <Conversations
          conversations={designer.saved}
          current={designer.conversationId}
          listed={designer.listed}
          onForget={(id) => void designer.forget(id)}
          onNew={designer.restart}
          onOpen={(id) => void designer.open(id)}
          onToggle={() => setShowing((on) => !on)}
          open={showing}
        />

        <SplitPane
          label="How much room the conversation gets against the design"
          start={
            <section className="flex min-h-0 flex-1 flex-col rounded-card border border-line bg-surface p-5">
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
                {designer.turns.length === 0 ? <Opening /> : null}
                {designer.turns.map((turn) => (
                  <Said key={turn.id} turn={turn} />
                ))}
                {designer.state.status === 'thinking' ? (
                  <p className="text-[13px] text-t3">Designing…</p>
                ) : null}
              </div>

              <div className="mt-4 border-line border-t pt-4">
                <Field
                  id="designer-instruction"
                  label="What should it say?"
                  type="text"
                  autoComplete="off"
                  value={instruction}
                  disabled={designer.state.status === 'thinking'}
                  hint="A launch announcement, a monthly update, a welcome email — or a change to what is on the right."
                  onChange={setInstruction}
                />

                {/*
                  Beside the files rather than inside the instruction, because
                  it is the same kind of thing: something to design *from*,
                  which would otherwise have to be described in words. A person
                  who can paste their website should never have to read a hex
                  code off it first.
                */}
                <Field
                  id="designer-site"
                  label="Match a website"
                  type="text"
                  autoComplete="off"
                  optional
                  value={designer.siteUrl}
                  disabled={designer.state.status === 'thinking'}
                  hint="Paste an address and this reads its colours, its logo and its type, then designs to match. Used on the next message only — after that, ask for changes."
                  onChange={designer.setSiteUrl}
                />

                <Attachments
                  busy={designer.state.status === 'thinking'}
                  onAdd={(files) => void designer.addFiles(files)}
                  onRemove={designer.removeFile}
                  refused={designer.refused}
                  references={designer.references}
                />

                <LearnedBrand client={client} />
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    disabled={instruction.trim() === '' || designer.state.status === 'thinking'}
                    onClick={ask}
                    className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {designer.state.status === 'thinking' ? 'Designing…' : 'Send'}
                  </button>
                  {designer.turns.length === 0 ? null : (
                    <button
                      type="button"
                      onClick={designer.restart}
                      className="font-medium text-[13px] text-t3 hover:text-t1"
                    >
                      Start again
                    </button>
                  )}
                </div>
              </div>
            </section>
          }
          end={
            <section className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-card border border-line bg-surface p-5">
              <Preview client={client} designer={designer} />
            </section>
          }
        />
      </div>
    </div>
  )
}

type Designer = ReturnType<typeof useDesigner>

function Problem({ state }: { readonly state: DesignerState }) {
  switch (state.status) {
    /*
     * A deployment fact rather than a failure, and said as one. Offering a
     * retry would be offering something that cannot work until somebody sets a
     * variable on the server.
     */
    case 'not-configured':
      return (
        <Alert tone="unknown">
          The AI designer is not switched on yet. It needs an Anthropic API key on the server —
          until then, the block builder does everything except the writing.
        </Alert>
      )
    case 'busy':
      return (
        <Alert tone="unknown">The designer is busy right now. Send that again in a moment.</Alert>
      )
    case 'failed':
      return <Alert>We could not reach the designer. Please try again in a moment.</Alert>
    default:
      return null
  }
}

function Opening() {
  return (
    <div className="rounded-card bg-surface-2 p-4">
      <p className="text-[13px] text-t2 leading-relaxed">
        Tell me what you want — a newsletter, a launch, a welcome email. Say who it is for and what
        it should get across, and I will lay one out. Then ask for changes: “make the header green”,
        “add a button to the pricing page”, “shorter”.
      </p>
    </div>
  )
}

function Said({ turn }: { readonly turn: Turn }) {
  return turn.from === 'person' ? (
    <p className="ml-8 rounded-card bg-brand-soft px-3 py-2.5 text-[13px] text-brand-strong">
      {turn.text}
    </p>
  ) : (
    <p className="mr-8 rounded-card bg-surface-2 px-3 py-2.5 text-[13px] text-t2 leading-relaxed">
      {turn.text}
    </p>
  )
}

/**
 * The design, and the two things worth doing with it.
 *
 * Saving needs a name and a subject, which the model was never asked for — so
 * they are asked for here rather than invented. A template called "Untitled" is
 * one nobody finds again.
 */
function Preview({
  client,
  designer,
}: {
  readonly client: ApiClient
  readonly designer: Designer
}) {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [subject, setSubject] = useState('')
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [saved, setSaved] = useState<string | undefined>(undefined)

  const keep = useCallback(async () => {
    const template = designer.asTemplate(name.trim(), subject.trim())
    if (template === undefined) return

    setSaving(true)
    setProblem(undefined)
    try {
      const outcome = await client.saveTemplate(template)
      if (outcome.status === 'name-taken') {
        setProblem('A template with that name already exists.')
        return
      }
      setSaved(template.name)
    } catch {
      setProblem('We could not save that template. Please try again in a moment.')
    } finally {
      setSaving(false)
    }
  }, [client, designer, name, subject])

  if (designer.design === undefined) {
    return (
      <>
        <h2 className="mb-3 font-bold text-[15px] text-t1">Preview</h2>
        <p className="flex flex-1 items-center justify-center rounded-field border border-line border-dashed px-4 text-center text-[13px] text-t3">
          Your design will appear here.
        </p>
      </>
    )
  }

  return (
    <>
      <h2 className="mb-3 font-bold text-[15px] text-t1">Preview</h2>

      {problem === undefined ? null : <Alert>{problem}</Alert>}
      {saved === undefined ? null : <Alert tone="ok">Saved as “{saved}”.</Alert>}

      <iframe
        title="Designed email"
        srcDoc={designer.design}
        sandbox=""
        className="min-h-0 w-full flex-1 rounded-field border border-line bg-white"
      />

      <div className="mt-4 grid gap-3 border-line border-t pt-4 sm:grid-cols-2">
        <Field
          id="designer-name"
          label="Template name"
          type="text"
          autoComplete="off"
          value={name}
          disabled={saving}
          onChange={setName}
        />
        <Field
          id="designer-subject"
          label="Subject"
          type="text"
          autoComplete="off"
          value={subject}
          disabled={saving}
          onChange={setSubject}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={saving || name.trim() === '' || subject.trim() === ''}
          onClick={() => void keep()}
          className="rounded-pill bg-brand px-4 py-2 font-semibold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save to templates'}
        </button>
        {/*
          A design taken into a campaign is a draft to edit, not a send. It
          opens in the builder like any other, because that is what it is.
        */}
        <button
          type="button"
          disabled={saving}
          onClick={() => void navigate('/templates')}
          className="font-medium text-[13px] text-t3 hover:text-t1 disabled:opacity-60"
        >
          Go to templates
        </button>
      </div>
    </>
  )
}
